import React from 'react';
import PropTypes from 'prop-types';

import Style from './ticketDetail.module.css';
import { capitalizeFirstLetter, translateLocale } from '../utils';
import { useTranslation } from 'react-i18next';
import LabelDetails from '../label-details/labelDetails';
import TicketCard from '../ticket-card/ticketCard';
import { Status, Numbers, Initials, CopyToClipboardMinimal } from '@weezevent/nacre';
import { AttendeeStatus } from '../../../list/components/attendee-row/attendee-row';
import QRCode from 'react-qr-code';
import moment from 'moment-timezone';
import useAppContext from '../../../../../context/useAppContext';
import { AttendeeModerationStatus } from '../../../list/components/moderation-status';
import { formatDateForInventories } from '../../../../../utils/date';
function TicketLabelDetails({ title = null, subtitle = null }) {
    return <LabelDetails title={title}>{subtitle && <div className={Style['medium']}>{subtitle}</div>}</LabelDetails>;
}

function TicketHeader({ attendee, sync = () => {} }) {
    const { t } = useTranslation();
    const [{ event }] = useAppContext();

    if (!attendee) {
        return null;
    }

    return (
        <TicketCard.Header>
            <div
                className={`${Style['ticket-header__container-attendee-infos']} ${Style['white']} ${
                    event.is_moderation_enabled ? Style['ticket-header__container-attendee-infos__moderation'] : ''
                }`}
            >
                <div className={Style['ticket-header__container-attendee-name']}>
                    <Initials content={attendee.resolved_name} size="big" className={Style['ticket-header__initials']} />

                    <div className={Style['ticket-header__attendee-name']}>
                        <span>{attendee.resolved_name}</span>
                    </div>
                </div>
                {event.is_moderation_enabled && (
                    <div className={Style['attendee-moderation-status']}>
                        {t('ticket.attendee.moderation')}:
                        <AttendeeModerationStatus className={Style['ticket_header__moderation_dropdown']} attendee={attendee} sync={sync} />
                    </div>
                )}
            </div>

            <LabelDetails.Container>
                <TicketLabelDetails title={t('ticket.common.rate')} subtitle={attendee.rate.name} />
                {attendee.rate.group && (
                    <TicketLabelDetails
                        title={t('ticket.common.group')}
                        subtitle={attendee.rate.group.group_template ? attendee.rate.group.group_template.name : attendee.rate.group.name}
                    />
                )}
            </LabelDetails.Container>
        </TicketCard.Header>
    );
}

function TicketContent({ attendee, locale, event }) {
    const { t } = useTranslation();

    if (!attendee || !event) {
        return null;
    }

    return (
        <TicketCard.Content className={Style['ticket-content__primary-infos']}>
            <LabelDetails.Container>
                <LabelDetails title={t('ticket.common.price')}>
                    <Numbers className={Style['ticket-content__price']} value={attendee.paid_price} locale={locale} currency={event?.currency || 'EUR'} showNullDecimal />
                </LabelDetails>
                <LabelDetails title={t('ticket.common.status')}>
                    <AttendeeStatus attendee={attendee} />
                </LabelDetails>
                <LabelDetails title={t('ticket.common.composted')}>
                    <Status color="grey" text={attendee.scanned ? t('ticket.common.scanned') : t('ticket.common.notScanned')} />
                </LabelDetails>
            </LabelDetails.Container>
        </TicketCard.Content>
    );
}

function TicketFurtherInformation({ attendee, locale, event }) {
    const { t } = useTranslation();

    const [{ event_timezone }] = useAppContext();

    const sessionDate = React.useMemo(() => {
        if (!attendee?.booking || !event.use_sessions || !attendee?.booking.inventory_weez_date_id || !attendee.booking.inventory_start_date) {
            return null;
        }

        let dateLocalized = moment.tz(
            moment
                .utc(attendee.booking.inventory_start_date, moment.ISO_8601)
                .tz(event_timezone)
                .format('YYYY-MM-DD HH:mm'),
            event_timezone
        );

        return new Intl.DateTimeFormat(locale, { timeStyle: 'short', dateStyle: 'short' }).format(new Date(dateLocalized));
    }, [attendee, event, locale, event_timezone]);

    const inventoryName = React.useMemo(() => {
        if (!attendee?.booking || event?.use_sessions || (event?.isFakeSeating() && !event?.isFakeSeatingMultipleInventories())) return null;
        return event?.isFakeSeating() ? formatDateForInventories(attendee.booking.inventory_start_date, locale, event.timezone) : attendee.booking.inventory_name;
    }, [attendee, event, locale]);

    if (!attendee || !event) {
        return null;
    }

    return (
        <TicketCard.Content ticket>
            <div className={Style['medium']}>{t('ticket.common.furtherInformation')}</div>
            <LabelDetails.Container>
                <TicketLabelDetails title={t('ticket.common.language')} subtitle={capitalizeFirstLetter(translateLocale(locale, attendee.order.locale))} />
                {attendee.membership_code && <TicketLabelDetails title={t('ticket.common.membershipCode')} subtitle={attendee.membership_code} />}
                {attendee.partner_name && <TicketLabelDetails title={t('ticket.common.partner')} subtitle={attendee.partner_name} />}
                {attendee.booking && !event.use_sessions && (
                    <>
                        {inventoryName && <TicketLabelDetails title={t('ticket.common.inventory')} subtitle={inventoryName} />}
                        {!event.isFakeSeating() && <TicketLabelDetails title={t('ticket.common.contingent')} subtitle={attendee.booking.contingent_name || '-'} />}
                    </>
                )}
                {sessionDate && <TicketLabelDetails title={t('ticket.common.sessions')} subtitle={sessionDate} />}
                {attendee.order.discount && <TicketLabelDetails title={t('ticket.common.promocode')} subtitle={attendee.order.discount.name} />}
            </LabelDetails.Container>
        </TicketCard.Content>
    );
}

function TicketFooter({ attendee }) {
    const { t } = useTranslation();

    if (!attendee) {
        return null;
    }

    return (
        <TicketCard.Footer className={Style['ticket-footer']}>
            <div className={Style['ticket-footer__qr-code']}>
                <QRCode value={attendee.barcode} size={45} />
            </div>
            <LabelDetails title={t('ticket.common.barcode')}>
                <div className={Style['ticket-footer__barcode-container']}>
                    <div className={Style['medium']}>
                        <CopyToClipboardMinimal>{attendee.barcode}</CopyToClipboardMinimal>
                    </div>
                    <div className={Style['ticket-footer__id-ticket']}>
                        N°<CopyToClipboardMinimal>{attendee.id_weez_ticket}</CopyToClipboardMinimal>
                    </div>
                </div>
            </LabelDetails>
        </TicketCard.Footer>
    );
}

export default function TicketDetail({ attendee = null, event = null, locale = 'en-GB', sync = () => {} }) {
    const backgroundClasses = {
        rejected: 'ticket-header__declined',
        validated: 'ticket-header__accepted',
        uncomplete: 'ticket-header__uncomplete',
        pending: 'ticket-header__pending'
    };
    const _moderationClassName = React.useMemo(() => {
        return event.is_moderation_enabled ? backgroundClasses[attendee.moderation_status] : '';
    }, [attendee, event]);

    return (
        <TicketCard className={Style[`${_moderationClassName}`]} id="ticket">
            <TicketHeader attendee={attendee} sync={sync} />
            <TicketContent attendee={attendee} event={event} locale={locale} />
            <TicketFurtherInformation attendee={attendee} event={event} locale={locale} />
            <TicketFooter attendee={attendee} />
        </TicketCard>
    );
}

TicketDetail.propTypes = {
    attendee: PropTypes.object,
    event: PropTypes.object,
    locale: PropTypes.string
};
